import { useCallback, useMemo } from 'react';

import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import type { ReverseAssociationTypeResolver } from '@/lib/proposals/utils/derivePreviewOverlays';

const EMPTY_REVERSE_NAMES: ReadonlyMap<string, string> = new Map();

/**
 * The `resolveReverseAssociationTypeName` argument `derivePreviewOverlays`
 * takes, built from the org's association-type schema.
 *
 * The derivation module is schema-free, but its reverse pass has to re-type
 * each edge to the target's own association type — the relationship carries a
 * different name (and label) viewed from the other end. The schema query is
 * shared app-wide via React Query, so it is already warm on any surface that
 * has rendered CRM associations.
 *
 * Shared by every surface that derives overlays from a proposal (the
 * action-center preview provider, the builder subject) so they can never
 * disagree on the inverse of an edge.
 */
export function useReverseAssociationTypeResolver(
  orgId: number | undefined
): ReverseAssociationTypeResolver {
  const { associationTypes } = useAssociationTypes(orgId);
  const reverseNameByType = useMemo(() => {
    if (!associationTypes || associationTypes.length === 0) {
      return EMPTY_REVERSE_NAMES;
    }
    const map = new Map<string, string>();
    for (const at of associationTypes) {
      if (at.reverse_association_type_name) {
        map.set(at.association_type_name, at.reverse_association_type_name);
      }
    }
    return map;
  }, [associationTypes]);
  return useCallback(
    (associationTypeName: string) => reverseNameByType.get(associationTypeName),
    [reverseNameByType]
  );
}
